import { useState } from 'react';
import { useAppTranslation } from '../hooks/useAppTranslation.js';
import { useTier } from '../hooks/useTier.js';
import { TIER_PRICING, type Tier } from '@vcard/shared';
import Layout from '../components/Layout.js';
import { PlansOverview } from '../components/PlansOverview.js';
import { SeatAssignment } from '../components/SeatAssignment.js';
import { SelfUpgrade } from '../components/SelfUpgrade.js';
import { useAuth } from '../contexts/AuthContext.js';

const USERS_PACKAGE_PRICE_ID = import.meta.env['VITE_STRIPE_PRICE_USERS_PACKAGE'] as string ?? '';
const USERS_PACKAGE_SIZE = 10;
const USERS_PACKAGE_PRICE_EUR = 29;

// Preset packages
const PRESETS = [
  { multiplier: 1, slots: 10, price: 29 },
  { multiplier: 2, slots: 20, price: 58 },
  { multiplier: 3, slots: 30, price: 87 },
];

export default function Billing() {
  const { t } = useAppTranslation(); const { tier, baseTier, override, status, trialDaysRemaining, locked, seats, seatTier, limits, usage, loading, refresh } = useTier();
  const { user } = useAuth();
  const [purchasing, setPurchasing] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<number | null>(1);
  const [customSlots, setCustomSlots] = useState<number>(10);

  async function handleBuyUsers(quantity: number) {
    setPurchasing(true);
    try {
      const res = await fetch('/api/billing/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          priceId: USERS_PACKAGE_PRICE_ID,
          packageQuantity: quantity,
          successUrl: `${window.location.origin}/billing?success=1`,
          cancelUrl: `${window.location.origin}/billing`,
        }),
      });
      const { checkoutUrl } = await res.json() as { checkoutUrl: string };
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      }
    } finally {
      setPurchasing(false);
    }
  }

  async function handleManageBilling() {
    const res = await fetch('/api/billing/portal-session', {
      method: 'POST',
      credentials: 'include',
    });
    const { portalUrl } = await res.json() as { portalUrl: string };
    window.location.href = portalUrl;
  }

  if (loading) {
    return (
      <Layout title="Billing & Plan">
        <div className="p-8 text-center text-ink-faint">{t('billing.states.loading')}</div>
      </Layout>
    );
  }

  const pricingLabel = baseTier === 'free' && !override ? t('plans.trialName', 'Testmonat') : TIER_PRICING[tier].label;
  const userLimit = limits.users ?? 0;
  const userCount = usage.users ?? 0;
  const slotsRemaining = Math.max(0, userLimit - userCount);

  // Calculate price and slots based on selection
  const activeSlots = selectedPreset !== null ? selectedPreset * USERS_PACKAGE_SIZE : customSlots;
  const activePrice = selectedPreset !== null ? selectedPreset * USERS_PACKAGE_PRICE_EUR : Math.ceil(customSlots * 2.9);
  const afterPurchaseSlots = userCount + activeSlots;

  function handlePresetClick(multiplier: number) {
    setSelectedPreset(multiplier);
  }

  function handleCustomInput(e: React.ChangeEvent<HTMLInputElement>) {
    const val = Math.max(1, parseInt(e.target.value) || 1);
    setCustomSlots(val);
    setSelectedPreset(null);
  }

  function handleBuy() {
    const quantity = selectedPreset !== null ? selectedPreset : Math.ceil(customSlots / USERS_PACKAGE_SIZE);
    handleBuyUsers(quantity);
  }

  return (
    <Layout title="Billing & Plan">
      <div className="max-w-6xl space-y-5">

        {/* Success message */}
        {typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('success') === '1' && (
          <div className="rounded-xl bg-success-100 dark:bg-success-500/10 border border-success-500/30 p-4 text-success-400 text-sm">
            {t('billing.success.slotsActivated')}
          </div>
        )}

        {/* Free test period over */}
        {locked && (
          <div role="alert" className="rounded-xl border border-danger-500/30 bg-danger-100 dark:bg-danger-500/10 p-4 text-sm text-danger-700 dark:text-danger-300">
            {t('billing.locked', 'Dein Testmonat ist abgelaufen. Deine Karten zeigen jetzt den Hinweis „Unbezahlte Version“. Buche einen Plan, dann sind sie unter demselben Link und QR-Code sofort wieder erreichbar.')}
          </div>
        )}

        {/* Current plan and usage in one row, so the plans are visible without scrolling */}
        <div className="rounded-xl bg-panel border border-line px-5 py-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <p className="text-xs text-ink-muted uppercase tracking-wider">{t('billing.currentPlan')}</p>
            <p className="mt-0.5 text-xl font-semibold text-ink">
              {seatTier && baseTier !== 'free' ? TIER_PRICING[seatTier].label : pricingLabel}
              {status && baseTier !== 'free' && (
                <span className="ml-2 text-sm font-normal text-ink-muted capitalize">{status.replace('_', ' ')}</span>
              )}
            </p>
            {baseTier === 'free' && !override && !locked && trialDaysRemaining !== null && (
              <p className="mt-0.5 text-sm text-accent-700 dark:text-accent-300">
                {t('billing.trialRunning', 'Noch {{days}} Tage kostenlos. Danach brauchst du einen Plan, sonst zeigen deine Karten den Hinweis „Unbezahlte Version“.', { days: trialDaysRemaining })}
              </p>
            )}
            {override && (
              <p className="mt-1 inline-flex flex-wrap items-center gap-1 rounded-lg bg-accent-100 dark:bg-accent-500/15 px-2.5 py-1 text-xs text-accent-700 dark:text-accent-300">
                {t('billing.unlock.active', 'Freigeschaltet: {{tier}}', { tier: pricingLabel })}
                {' · '}
                {override.until
                  ? t('billing.unlock.until', 'bis {{date}}', { date: new Date(override.until).toLocaleDateString('de-DE', { timeZone: 'UTC' }) })
                  : t('billing.unlock.unlimited', 'ohne Ablaufdatum')}
                {override.note ? ` · ${override.note}` : ''}
              </p>
            )}
            {baseTier !== 'free' && trialDaysRemaining !== null && trialDaysRemaining > 0 && (
              <p className="mt-0.5 text-sm text-warning-400">
                {t('billing.trialEnding', 'Testphase endet in {{days}} Tag(en)', { days: trialDaysRemaining })}
              </p>
            )}
          </div>

          <div className="w-full sm:w-72">
            <UsageStat
              label={t('billing.usage.users', 'Nutzer')}
              current={userCount}
              limit={userLimit}
              remaining={slotsRemaining}
            />
          </div>

          {seats.starter + seats.business > 0 && (seatTier === 'starter' || (user?.role === 'owner' && seats.usedStarter > 0)) && (
            <SelfUpgrade seats={seats} userCount={usage.users} isOwner={user?.role === 'owner'} onStarter={seatTier === 'starter'} onChanged={refresh} />
          )}

          {baseTier !== 'free' && (
            <button
              onClick={handleManageBilling}
              className="rounded-full border border-line px-4 py-2 text-sm text-ink-muted hover:text-ink hover:border-line-strong transition-all whitespace-nowrap"
            >
              {t('billing.manage', 'Abo verwalten')}
            </button>
          )}
        </div>

        <PlansOverview seats={seats} isTrial={baseTier === 'free' && !override} />

        {user?.role === 'owner' && <SeatAssignment interval={seats.interval} onChanged={refresh} />}

        {/* Buy more users (only when a user-package price is configured) */}
        {USERS_PACKAGE_PRICE_ID && (
        <div className="space-y-4">
          <h2 className="text-sm font-semibold text-ink-muted uppercase tracking-wider">{t('billing.addUsers.title')}</h2>
          <div className="rounded-xl bg-panel border border-accent-500/20 p-6">
            <p className="text-sm text-ink-muted mb-4">
              {t('billing.addUsers.description')}
            </p>

            {/* Package options with selection state */}
            <div className="mt-4 grid grid-cols-3 gap-3">
              {PRESETS.map((preset) => {
                const isSelected = selectedPreset === preset.multiplier;
                return (
                  <button
                    key={preset.multiplier}
                    onClick={() => handlePresetClick(preset.multiplier)}
                    disabled={purchasing}
                    className={`rounded-lg border px-3 py-2 text-center transition-all disabled:opacity-50 ${
                      isSelected
                        ? 'border-accent-500 bg-accent-500/20'
                        : 'border-line hover:border-accent-500/40'
                    }`}
                  >
                    <p className="text-sm font-semibold text-ink">{preset.slots} Slots</p>
                    <p className="text-xs text-ink-muted">€{preset.price}/Mo</p>
                  </button>
                );
              })}
            </div>

            {/* Custom amount input */}
            <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="flex items-center gap-2">
                <label className="text-xs text-ink-muted">{t('billing.addUsers.customLabel')}</label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={customSlots}
                  onChange={handleCustomInput}
                  className="w-20 rounded-lg border border-line bg-panel px-3 py-1.5 text-sm text-ink"
                />
                <span className="text-xs text-ink-muted">Slots</span>
              </div>
              {selectedPreset === null && (
                <span className="text-sm text-ink-muted">= <strong className="text-ink">€{activePrice}/Monat</strong></span>
              )}
            </div>

            {/* Price summary */}
            <div className="mt-4 rounded-lg border border-line bg-raised p-4">
              <div className="space-y-1 text-sm">
                <p className="text-ink-muted">
                  <span className="text-ink-muted">Aktuell:</span> {userCount} Benutzer <span className="text-ink-faint">(Limit: {userLimit})</span>
                </p>
                <p className="text-ink-muted">
                  <span className="text-ink-muted">Nach Kauf:</span> <span className="text-ink font-medium">{afterPurchaseSlots} Benutzer</span>
                </p>
                <p className="text-ink-muted">
                  <span className="text-ink-muted">Preis:</span> <span className="text-ink font-semibold">€{activePrice}/Monat</span>
                </p>
              </div>
            </div>

            {/* Buy button */}
            <div className="mt-4">
              <button
                onClick={handleBuy}
                disabled={purchasing}
                className="w-full sm:w-auto rounded-full bg-accent-600 px-6 py-3 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 transition-opacity whitespace-nowrap"
              >
                {purchasing ? 'Lädt…' : `Kaufen – €${activePrice}/Monat`}
              </button>
            </div>
          </div>
        </div>
        )}
      </div>
    </Layout>
  );
}

function UsageStat({
  label, current, limit, remaining,
}: {
  label: string; current: number; limit: number; remaining?: number;
}) {
  const pct = limit ? Math.round((current / limit) * 100) : 0;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="text-ink-muted">{label}</span>
        <span className="text-ink-muted font-medium">{current} / {limit}</span>
      </div>
      {limit && (
        <div className="h-1.5 w-full rounded-full bg-raised overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{
              width: `${Math.min(pct, 100)}%`,
              background: pct >= 90
                ? 'linear-gradient(90deg, #f97316, #ef4444)'
                : 'rgb(var(--accent-500))',
            }}
          />
        </div>
      )}
    </div>
  );
}
